import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSales } from '@/hooks/useSales';
import { CreditCard, Banknote, FileText, MoreHorizontal } from 'lucide-react';
export const PaymentMethodChart: React.FC = () => {
  const {
    sales
  } = useSales();
  const topProductsData = useMemo(() => {
    const productStats = sales.reduce((acc, sale) => {
      const productName = sale.product_name;
      if (!acc[productName]) {
        acc[productName] = { 
          revenue: 0, 
          quantity: 0,
          product: sale
        };
      }
      acc[productName].revenue += sale.total_amount;
      acc[productName].quantity += sale.quantity_sold;
      return acc;
    }, {} as Record<string, { revenue: number; quantity: number; product: any }>);

    // Convertir en array et trier par CA
    const productArray = Object.entries(productStats)
      .map(([name, stats]) => ({
        name,
        value: stats.revenue,
        quantity: stats.quantity,
        product: stats.product
      }))
      .sort((a, b) => b.value - a.value);

    // Prendre les top 8 produits
    const topProducts = productArray.slice(0, 8);
    
    // Si il y a plus de 8 produits, grouper le reste dans "Autres"
    if (productArray.length > 8) {
      const othersRevenue = productArray.slice(8).reduce((sum, item) => sum + item.value, 0);
      const othersQuantity = productArray.slice(8).reduce((sum, item) => sum + item.quantity, 0);
      
      if (othersRevenue > 0) {
        topProducts.push({
          name: 'Autres',
          value: othersRevenue,
          quantity: othersQuantity,
          product: null
        });
      }
    }

    return topProducts;
  }, [sales]);

  // Couleurs pour le camembert (même style que RetraitAnalytics)
  const pieColors = [
    'hsl(220 91% 42%)',  // Bleu professionnel
    'hsl(142 76% 36%)',  // Vert
    'hsl(35 90% 55%)',   // Orange
    'hsl(0 75% 55%)',    // Rouge
    'hsl(271 91% 65%)',  // Violet
    'hsl(45 93% 47%)',   // Jaune
    'hsl(340 82% 52%)',  // Rose
    'hsl(200 98% 39%)',  // Cyan
    'hsl(20 90% 48%)',   // Orange brûlé
    'hsl(210 40% 60%)'   // Gris pour "Autres"
  ];
  const totalRevenue = topProductsData.reduce((sum, item) => sum + item.value, 0);
  const totalQuantity = topProductsData.reduce((sum, item) => sum + item.quantity, 0);
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };
  const CustomTooltip = ({
    active,
    payload
  }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const revenuePercentage = totalRevenue > 0 ? (data.value / totalRevenue * 100).toFixed(1) : 0;
      const quantityPercentage = totalQuantity > 0 ? (data.quantity / totalQuantity * 100).toFixed(1) : 0;
      
      return <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium mb-2">{data.name}</p>
          <div className="space-y-1">
            <p className="text-primary font-semibold">CA: {formatPrice(data.value)}</p>
            <p className="text-sm text-muted-foreground">{revenuePercentage}% du CA total</p>
            <p className="text-secondary font-medium">Quantité: {data.quantity}</p>
            <p className="text-sm text-muted-foreground">{quantityPercentage}% des ventes</p>
          </div>
        </div>;
    }
    return null;
  };
  if (topProductsData.length === 0) {
    return <Card className="h-full">
        <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-gradient-to-br from-violet-500/10 to-violet-600/20 transition-transform duration-200 hover:scale-105">
            <CreditCard className="w-6 h-6 text-violet-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium mb-1 text-violet-700 dark:text-violet-300">
              Top Ventes
            </p>
            <p className="text-xl font-bold text-violet-800 dark:text-violet-200">
              Répartition par produit
            </p>
          </div>
        </div>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[200px]">
          <p className="text-muted-foreground text-sm text-center">
            Aucune vente enregistrée
          </p>
        </CardContent>
      </Card>;
  }
  return <Card className="">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-gradient-to-br from-violet-500/10 to-violet-600/20 transition-transform duration-200 hover:scale-105">
            <CreditCard className="w-6 h-6 text-violet-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium mb-1 text-violet-700 dark:text-violet-300">
              Top Ventes
            </p>
            <p className="text-xl font-bold text-violet-800 dark:text-violet-200">
              Répartition par produit
            </p>
          </div>
        </div>
        <div className="text-xs text-violet-600 dark:text-violet-400 mt-1 space-y-1">
          <p>CA total : {formatPrice(totalRevenue)}</p>
          <p>Quantité totale : {totalQuantity} articles</p>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[500px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={topProductsData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, value, percent }) => {
                  return `${name} (${(percent * 100).toFixed(0)}%)`;
                }}
                outerRadius={140}
                fill="#8884d8"
                dataKey="value"
                stroke="white"
                strokeWidth={2}
              >
                {topProductsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>;
};